
import { defineComponent } from 'vue'
import { containsElement } from '~/utils/ui'

export default defineComponent({
  name: 'SidebarMenu',

  props: {
    width: {
      type: String,
      default: 'lg:w-64'
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    collapsible: {
      type: Boolean,
      default: true
    },
    widthCollapsed: {
      type: String,
      default: 'lg:w-16'
    },
    direction: {
      type: String,
      default: 'left'
    },
    footerClass: {
      type: String,
      default: ''
    },
    footerBrandClass: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      isCollapsed: this.collapsed,
      isOpen: false,
      largeScreenSize: 1024
    }
  },

  computed: {
    directionClasses(): string {
      const directionStyle: Record<string, string> = {
        left: 'lg:left-0 -left-3/4',
        right: 'lg:right-0 -right-3/4'
      }
      const openDirections: Record<string, string> = {
        left: 'left-0',
        right: 'right-0'
      }
      if (this.isOpen) {
        return openDirections[this.direction]
      }
      return directionStyle[this.direction]
    },

    borderClasses(): string {
      const borders: Record<string, string> = {
        left: 'border-r',
        right: 'border-l'
      }
      if (!this.isOpen) return borders[this.direction]
      return ''
    },

    modalWidth(): string {
      if (this.isCollapsed && this.collapsible) return `w-9/12 ${this.widthCollapsed}`
      return `w-9/12 ${this.width}`
    },

    hoverStyle(): string {
      const directionStyle: Record<string, string> = {
        left: '-right-3',
        right: '-left-3'
      }
      return directionStyle[this.direction]
    },

    arrow(): string {
      if (
        (this.direction === 'left' && !this.isCollapsed) ||
        (this.direction === 'right' && this.isCollapsed)
      ) {
        return 'chevron-left'
      }
      return 'chevron-right'
    }
  },

  mounted() {
    this.$root.$on('ui:show-sidebar-menu', () => {
      this.isCollapsed = false
      this.isOpen = true
      this.$emit('open')
    })

    this.$root.$on('ui:hide-sidebar-menu', () => {
      this.isCollapsed = true
      this.isOpen = false
      this.$emit('collapse')
    })

    this.$root.$on('ui:toggle-sidebar-menu', () => {
      this.isCollapsed = !this.isCollapsed
      this.isOpen = !this.isOpen
      this.$emit(this.isCollapsed ? 'collapse' : 'open')
    })

    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        if (window.innerWidth < this.largeScreenSize) {
          this.isOpen = false
          this.isCollapsed = false
          this.$emit('collapse', this.isCollapsed)
        }
      })
    })
  },

  beforeDestroy() {
    this.$root.$off('ui:show-sidebar-menu')
    this.$root.$off('ui:hide-sidebar-menu')
    this.$root.$off('ui:toggle-sidebar-menu')
  },

  methods: {
    collapseSidebar(): void {
      this.isCollapsed = !this.isCollapsed
      this.$emit('collapse', this.isCollapsed)
    },

    openModal(): void {
      this.isOpen = true
      this.$emit('open')
    },

    closeSidebar(event: Event): void {
      if (event?.target) {
        const target = event.target as HTMLElement
        const toggleButton = document.getElementById('mobile-menu-toggle')
        if (!toggleButton) {
          this.toggleClose()
        } else if (!containsElement(toggleButton, target) && target.id !== 'mobile-menu-toggle') {
          this.toggleClose()
        }
      }
    },

    toggleClose() {
      this.isOpen = false
      this.$emit('close')
    }
  }
})
